"use client";

import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Grid,
  Text,
  Box,
  HStack,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAppTheme } from "../../ThemeContext.tsx";

interface DateRangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (startDate: Date, endDate: Date) => void;
  currentStartDate?: Date;
  currentEndDate?: Date;
}

export const DateRangeDialog: React.FC<DateRangeDialogProps> = ({
  isOpen,
  onClose,
  onApply,
  currentStartDate,
  currentEndDate,
}) => {
  const [selectedStartDate, setSelectedStartDate] = React.useState<Date | null>(
    null
  );
  const [selectedEndDate, setSelectedEndDate] = React.useState<Date | null>(
    null
  );
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define theme-based colors
  const modalBg = isDark ? "gray.800" : "white";
  const headerTextColor = isDark ? "white" : "gray.800";
  const calendarHoverBg = isDark ? "gray.700" : "gray.200";
  const selectedBg = isDark ? "gray.500" : "gray.400";
  const todayTextColor = isDark ? "gray.400" : "gray.600";
  const dayNamesColor = isDark ? "gray.400" : "gray.600";
  const selectedRangeText = isDark ? "white" : "gray.800";

  React.useEffect(() => {
    if (isOpen) {
      setSelectedStartDate(currentStartDate || null);
      setSelectedEndDate(currentEndDate || null);
      setCurrentMonth(currentStartDate || new Date());
    }
  }, [isOpen, currentStartDate, currentEndDate]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDayOfMonth };
  };

  const handleDateClick = (date: Date) => {
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else {
      if (date < selectedStartDate) {
        setSelectedStartDate(date);
        setSelectedEndDate(null);
      } else {
        setSelectedEndDate(date);
      }
    }
  };

  const handleApply = () => {
    if (selectedStartDate && selectedEndDate) {
      onApply(selectedStartDate, selectedEndDate);
      onClose();
    } else {
      toast({
        title: "Please select both start and end dates",
        status: "error",
        duration: 3000,
      });
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentMonth);
    const days = [];
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<Box key={`empty-${i}`} />);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isSelected =
        selectedStartDate &&
        date >= selectedStartDate &&
        (!selectedEndDate || date <= selectedEndDate);
      const isToday = new Date().toDateString() === date.toDateString();

      days.push(
        <Box
          key={day}
          p={2}
          cursor="pointer"
          textAlign="center"
          rounded="md"
          bg={isSelected ? selectedBg : "transparent"}
          color={isSelected ? "white" : isToday ? todayTextColor : "inherit"}
          _hover={{ bg: isSelected ? selectedBg : calendarHoverBg }}
          onClick={() => handleDateClick(date)}
        >
          {day}
        </Box>
      );
    }

    return days;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader color={headerTextColor}>Select Date Range</ModalHeader>
        <ModalBody color={headerTextColor}>
          <VStack spacing={4}>
            <HStack justify="space-between" w="full">
              <Button
                color={headerTextColor}
                size="sm"
                variant="ghost"
                onClick={() =>
                  setCurrentMonth(
                    new Date(
                      currentMonth.getFullYear(),
                      currentMonth.getMonth() - 1
                    )
                  )
                }
              >
                <ChevronLeft />
              </Button>
              <Text color={headerTextColor}>
                {currentMonth.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </Text>
              <Button
                color={headerTextColor}
                size="sm"
                variant="ghost"
                onClick={() =>
                  setCurrentMonth(
                    new Date(
                      currentMonth.getFullYear(),
                      currentMonth.getMonth() + 1
                    )
                  )
                }
              >
                <ChevronRight />
              </Button>
            </HStack>

            <Grid templateColumns="repeat(7, 1fr)" gap={1} w="full">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <Box key={day} p={2} textAlign="center" color={dayNamesColor}>
                  {day}
                </Box>
              ))}
              {renderCalendar()}
            </Grid>

            <Box w="full">
              <Text fontSize="sm" mb={2} color={headerTextColor}>
                Selected Range:
              </Text>
              <Text fontSize="sm" color={selectedRangeText}>
                {selectedStartDate
                  ? `${selectedStartDate.toLocaleDateString()} - ${
                      selectedEndDate
                        ? selectedEndDate.toLocaleDateString()
                        : "..."
                    }`
                  : "No dates selected"}
              </Text>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            variant="ghost"
            mr={3}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            isDisabled={!selectedStartDate || !selectedEndDate}
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
